import get from "lodash/get";
import React, { useEffect, useState } from "react";
import { Button, Modal, PlusIcon } from "@pd/pd-ui-kit";
import { Flex } from "antd";
import { useTranslation } from "react-i18next";

import StaffForm from "~/components/Forms/StaffForm";
import StaffSertificationForm from "~/components/Forms/StaffSertificationForm";
import Preloader from "~/components/Preloader";
import useDeleteEmployeeExpectations from "~/pages/Editor/EditorStaff/hooks/useDeleteEmployeeExpectations";
import useGetDealersCategories from "~/pages/Editor/EditorStaff/hooks/useGetDealersCategories";
import useGetExpectations from "~/pages/StaffBonus/hooks/useGetStaffExpectations";
import Wrapper from "~/shared/ui/Wrapper";
import {
  useGetLocationData,
  useGoToPreviousPage,
  useSetLocationData,
} from "~/hooks/locationData";
import { getIndicatorName, setDefaultSelectR } from "~/libs/getData";
import { useGetGroupEducationEmployeeExpectationLazyQuery } from "~/schemas/generated/graphql";
import { usePermissions } from "~/shared/context/PermissionContext";
import { ConfirmModal } from "~/shared/ui/ConfirmModal";
import { TableV2 } from "~/shared/ui/TableV2";

import { getEditorStaffColumns } from "./model/tableAdapter";
import {
  EDITOR_MANAGING_STAFF_INDICATORS_PERIOD_OPTIONS,
  EDITOR_MANAGING_STAFF_OPTIONS,
} from "./options";

import s from "./EditorStaff.less";

const EditorStaff = () => {
  const { t } = useTranslation();
  const language = localStorage.getItem("language");
  const setData = useSetLocationData();
  const { openModal, currentId } = useGetLocationData();
  const goBack = useGoToPreviousPage();
  const { hasPermission } = usePermissions();
  const {
    data,
    refetch,
    isLoading: loading,
    isRefetching,
  } = useGetExpectations();
  const { data: dataDealersCategories, isLoading: loadingDealersCategories } =
    useGetDealersCategories();

  const [
    getGroupEducationEmployeeExpectation,
    getGroupEducationEmployeeExpectationState,
  ] = useGetGroupEducationEmployeeExpectationLazyQuery({
    fetchPolicy: "no-cache",
  });
  const [
    groupEducationEmployeeExpectation,
    setGroupEducationEmployeeExpectationState,
  ] = useState<any>();

  useEffect(() => {
    if (openModal === "EditSertification")
      getGroupEducationEmployeeExpectation();
  }, [openModal]);

  useEffect(() => {
    if (getGroupEducationEmployeeExpectationState.data) {
      setGroupEducationEmployeeExpectationState(
        get(
          getGroupEducationEmployeeExpectationState,
          "data.EmployeeGetter.getGroupEducationEmployeeExpectation",
        ),
      );
    }
  }, [getGroupEducationEmployeeExpectationState]);

  const checkEmptiness = (name) => (name ? name.id : " - ");
  const checkEmptinessPeriod = (name) => (name ? name.period : " - ");

  const [editorStaff, setEditorStaff] = useState([]);

  useEffect(() => {
    if (data && Array.isArray(data.expectations)) {
      const editorStaffss = data.expectations
        .map(
          ({
            StandardCountIndicator,
            standardCountIndicatorId,
            StandardCertificationIndicator,
            StandardTrainingIndicator,
            educationRequirement,
            isEducationRequirementPercentage,
            isCertificationRequirementPercentage,
            certificationRequirement,
            positions,
            ...staff
          }) => {
            const sortedPositions = [...positions].sort((a, b) =>
              a.code.localeCompare(b.code),
            );

            return {
              ...staff,
              id: sortedPositions.map((position) => position.code).join(", "),
              positions: sortedPositions,
              educationRequirement,
              certificationRequirement,
              isEducationRequirementPercentage,
              isCertificationRequirementPercentage,
              generalName: sortedPositions
                .map((position) =>
                  language === "en" ? position.nameEn : position.name,
                )
                .join(", "),
              code: sortedPositions.map((position) => position.code).join(", "),
              StandardCountIndicator: checkEmptiness(standardCountIndicatorId),
              StandardTrainingIndicator: isEducationRequirementPercentage
                ? `${educationRequirement} / -`
                : `- / ${educationRequirement}`,
              StandardCertificationIndicator:
                isCertificationRequirementPercentage
                  ? `${certificationRequirement} / -`
                  : `- / ${certificationRequirement}`,
              StandardCountIndicatorOption: setDefaultSelectR(
                checkEmptiness(standardCountIndicatorId),
                getIndicatorName(standardCountIndicatorId),
              ),
              StandardCertificationIndicatorOption: setDefaultSelectR(
                checkEmptiness(StandardCertificationIndicator),
                getIndicatorName(StandardCertificationIndicator),
              ),
              StandardTrainingIndicatorOption: setDefaultSelectR(
                checkEmptiness(StandardTrainingIndicator),
                getIndicatorName(StandardTrainingIndicator),
              ),
              StandardCountIndicatorPeriod: checkEmptinessPeriod(
                standardCountIndicatorId,
              ),
              StandardCertificationIndicatorPeriod: checkEmptinessPeriod(
                StandardCertificationIndicator,
              ),
              StandardTrainingIndicatorPeriod: checkEmptinessPeriod(
                StandardTrainingIndicator,
              ),
              personStandard: StandardCountIndicator || " - ",
              percentCertification: StandardCertificationIndicator || " - ",
              percentTraining: StandardTrainingIndicator || " - ",
              uniqueKey: `${staff.instanceId}_${sortedPositions
                .map((p) => p.id)
                .join("_")}`,
            };
          },
        )
        .filter(Boolean)
        .filter(
          (item, index, self) =>
            index === self.findIndex((i) => i.uniqueKey === item.uniqueKey),
        )
        .sort((a, b) => a.code.localeCompare(b.code));

      setEditorStaff(editorStaffss);
    }
  }, [data, isRefetching]);

  const selectedStaff = editorStaff.find((i) => String(i.id) === currentId);

  const certificationStaff =
    openModal === "EditSertification"
      ? (selectedStaff ?? editorStaff[0])
      : undefined;

  const handleCloseModal = () => {
    setData("openModal", "", "openChildModal", "", "currentId", "");
  };

  const openAddModal = () => {
    setData("openModal", "Add");
  };

  const openAddSertificationModal = () => {
    setData("openModal", "EditSertification", "currentId", "");
  };

  const { mutate: deleteExpectations } = useDeleteEmployeeExpectations();
  const deleteListRating = () => {
    deleteExpectations(
      { instanceId: selectedStaff.instanceId },
      {
        onSuccess: () => {
          refetch();
        },
        onError: (error) => {
          console.error("Ошибка при обновлении:", error);
        },
      },
    );
    handleCloseModal();
  };

  const columns = getEditorStaffColumns(
    t,
    EDITOR_MANAGING_STAFF_OPTIONS,
    EDITOR_MANAGING_STAFF_INDICATORS_PERIOD_OPTIONS,
  );

  const handleRowEdit = (record: { id: string | number }) => {
    setData("openModal", "Edit", "currentId", record.id);
  };

  const handleRowDelete = (record: { id: string | number }) => {
    setData("openModal", "Sure", "currentId", record.id);
  };

  if (loadingDealersCategories || loading) {
    return <Preloader />;
  }

  return (
    <Wrapper.Content
      title={t("staff.managing_jobtitle")}
      txtBtn={t("ui.back")}
      goBackHandler={goBack}
      controlComponent={
        <Flex gap={10}>
          {hasPermission("employeeAccounting.createExpectations") && (
            <Button
              onClick={openAddModal}
              size={"middle"}
              icon={<PlusIcon size={16} />}
            >
              {t("ui.add")}
            </Button>
          )}
        </Flex>
      }
    >
      <Flex justify={"space-between"} className={s.actionsRow}>
        <Flex gap={10}>
          {hasPermission(
            "employeeAccounting.employee.expectation.group.update",
          ) && (
            <Button
              color={"secondary"}
              variant={"filled"}
              bold
              fontSize="12"
              size={"middle"}
              radius={30}
              onClick={openAddSertificationModal}
            >
              {t("editorStaff.staff_sertification")}
            </Button>
          )}
        </Flex>
      </Flex>
      <TableV2
        columns={columns}
        dataSource={editorStaff}
        rowKey="uniqueKey"
        loading={loading}
        onRowEdit={
          hasPermission("employeeAccounting.updateExpectations")
            ? handleRowEdit
            : undefined
        }
        onRowDelete={
          hasPermission("employeeAccounting.deleteExpectations")
            ? handleRowDelete
            : undefined
        }
        actionsFixed
        actionsPlacement="left"
      />
      <Modal
        open={openModal === "Add"}
        title={t("editorStaff.staff_add")}
        onCancel={handleCloseModal}
        size={"large"}
        closable
      >
        <StaffForm
          dataDealersCategories={dataDealersCategories?.categories}
          setCloseModal={handleCloseModal}
          refetch={refetch}
        />
      </Modal>

      <ConfirmModal
        open={openModal === "Sure"}
        text={t("editorStaff.staff_del")}
        onConfirm={deleteListRating}
        onCancel={handleCloseModal}
      />
      {!!selectedStaff && (
        <Modal
          open={openModal === "Edit"}
          title={t("editorStaff.staff_edit")}
          onCancel={handleCloseModal}
          size={"large"}
          closable
        >
          <StaffForm
            setCloseModal={handleCloseModal}
            dataDealersCategories={dataDealersCategories?.categories}
            refetch={refetch}
            Staff={selectedStaff}
          />
        </Modal>
      )}
      {!!certificationStaff &&
        hasPermission(
          "employeeAccounting.employee.expectation.group.update",
        ) && (
          <Modal
            open={openModal === "EditSertification"}
            title={t("editorStaff.staff_sertification")}
            onCancel={handleCloseModal}
            closable
          >
            {getGroupEducationEmployeeExpectationState.loading &&
            groupEducationEmployeeExpectation ? (
              <Preloader />
            ) : (
              <StaffSertificationForm
                refetch={refetch}
                refetchGroupEducationEmployeeExpectationState={
                  getGroupEducationEmployeeExpectationState.refetch
                }
                editorStaffOne={certificationStaff}
                handleCloseModal={handleCloseModal}
                groupEducationEmployeeExpectation={
                  groupEducationEmployeeExpectation
                }
                loading={getGroupEducationEmployeeExpectationState.loading}
              />
            )}
          </Modal>
        )}
    </Wrapper.Content>
  );
};

export default EditorStaff;
